// Lecture du registre public des entreprises (D-111) DEPUIS L'APPAREIL du bureau, comme la FFBB (D-113) :
// API « Recherche d'entreprises » de l'État, ouverte, sans clé (CORS permis), limitée à 7 requêtes par seconde
// et par adresse. Lecture polie : une requête à la fois, 0,4 s au moins entre deux (2,5 par seconde au plus),
// 40 pages de 25 entreprises au plus par lecture, une pause et un nouvel essai si l'API demande de ralentir.
// Pourquoi l'appareil plutôt que le serveur : le serveur local de fonctions ne sert que celles du dépôt principal
// (une fonction Deno ne serait testable qu'après fusion), la base n'a pas d'accès réseau, et chaque club lit sa
// zone à son rythme (le débit compte par adresse : chaque bureau a la sienne).
// minimal=true + include=matching_etablissements,complements : la réponse ne contient même pas les dirigeants.
// Jamais appelé par la démo ni par le banc (le club de démonstration est refusé ici et par la base).
import { parseNearPoint, type Point, type RegistryRow, type SkipReason } from '../domain/registre';

const BASE = 'https://recherche-entreprises.api.gouv.fr/near_point';
export const MAX_PAGES = 40;
const GAP_MS = 400;
// Les secteurs qui soutiennent un club de quartier ; à part, les auto-écoles (section P).
const QUERIES: Record<string, string>[] = [
  { activite_principale: '85.53Z' },
  { section_activite_principale: 'C,F,G,H,I,K,L,M,N,Q,S' },
];

export interface ReadProgress { page: number; pages: number; rows: number }
export interface ReadResult { rows: RegistryRow[]; skipped: Partial<Record<SkipReason, number>>; complete: boolean }

const sleep = (ms: number, signal?: AbortSignal) => new Promise<void>((res, rej) => {
  const t = setTimeout(res, ms);
  signal?.addEventListener('abort', () => { clearTimeout(t); rej(new DOMException('abandon', 'AbortError')); }, { once: true });
});

export async function readRegistry(o: {
  gym: Point; radiusKm: number; demo: boolean; signal?: AbortSignal; onProgress?: (p: ReadProgress) => void; fetchImpl?: typeof fetch; gapMs?: number;
}): Promise<ReadResult> {
  if (o.demo) throw new Error('démo : le registre n’est jamais lu');
  const { NAF_LABELS } = await import('../domain/naf-libelles');
  const f = o.fetchImpl ?? fetch;
  const seen = new Set<string>();
  const rows: RegistryRow[] = [];
  const skipped: Partial<Record<SkipReason, number>> = {};
  let used = 0, complete = true, last = 0;
  for (const q of QUERIES) {
    let page = 1, pages = 1;
    while (page <= pages) {
      if (used >= MAX_PAGES) { complete = false; break; }
      const wait = last + (o.gapMs ?? GAP_MS) - Date.now();
      if (wait > 0) await sleep(wait, o.signal);
      const url = new URL(BASE);
      const params = { lat: String(o.gym.lat), long: String(o.gym.lon), radius: String(o.radiusKm), page: String(page), per_page: '25', minimal: 'true',
        include: 'matching_etablissements,complements', limite_matching_etablissements: '20', ...q };
      for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
      let res: Response | null = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        last = Date.now();
        res = await f(url.toString(), { signal: o.signal, headers: { Accept: 'application/json' } });
        if (res.status !== 429 && res.status < 500) break;
        await sleep((o.gapMs ?? 2000) * (attempt + 1), o.signal);
      }
      used++;
      if (!res || !res.ok) throw new Error(`registre : ${res?.status ?? 'réseau'}`);
      const json = await res.json() as { total_pages?: number };
      pages = Math.max(1, Math.min(Number(json.total_pages) || 1, 400));
      const r = parseNearPoint(json, o.gym, o.radiusKm, NAF_LABELS);
      for (const [k, n] of Object.entries(r.skipped)) skipped[k as SkipReason] = (skipped[k as SkipReason] ?? 0) + (n ?? 0);
      for (const x of r.rows) if (!seen.has(x.siret)) { seen.add(x.siret); rows.push(x); }
      o.onProgress?.({ page: used, pages: Math.min(MAX_PAGES, used + Math.max(0, pages - page)), rows: rows.length });
      page++;
    }
  }
  return { rows, skipped, complete };
}
